import { GeneralButtonProps } from "@/app/interfaces/props/GeneralButtonProps";
import { Button as MuiButton, styled, CSSObject, alpha } from "@mui/material"; // Added CSSObject
import { brandColors } from "../../../../stories/helpers/ColorTokenHelper";
import { ColorTokensEnum } from "../../../../enums/tokens/colorTokensEnum";
import { ControlSizeTokensEnum } from "../../../../enums/tokens/controlSizeTokenEnum";
import { ButtonTypeTokensEnum } from "../../../../enums/tokens/buttonTypeTokensEnum";
import { StateTokensEnum } from "../../../../enums/tokens/stateTokensEnum";
import { TypographyTokensEnum } from "../../../../enums/tokens/typographyTokensEnum";
import typographyTokens from "../../../../stories/tokens/typography-tokens.json";
import { radii } from "../../../../stories/helpers/RadiusTokenHelper";
import { RadiusTokensEnum } from "../../../../enums/tokens/radiusTokensEnum";

const sizeTypographyMap = {
  [ControlSizeTokensEnum.SMALL]: TypographyTokensEnum.BUTTON_SMALL,
  [ControlSizeTokensEnum.DEFAULT]: TypographyTokensEnum.BUTTON_DEFAULT,
  [ControlSizeTokensEnum.LARGE]: TypographyTokensEnum.BUTTON_LARGE,
} as const;

const sizeDimensionsMap = {
  [ControlSizeTokensEnum.SMALL]: {
    minWidth: "120px",
    height: "32px",
    padding: "16px 12px",
  },
  [ControlSizeTokensEnum.DEFAULT]: {
    minWidth: "150px",
    height: "36px",
    padding: "16px 12px",
  },
  [ControlSizeTokensEnum.LARGE]: {
    minWidth: "175px",
    height: "42px",
    padding: "16px 12px",
  },
} as const;

const DEFAULT_ICON_SIZE = 20;

const StyledButton = styled(MuiButton, {
  shouldForwardProp: (prop) =>
    !["buttonType", "ownerState"].includes(prop as string),
})<{ ownerState: GeneralButtonProps }>(({ ownerState }): CSSObject => {
  const currentSize = ownerState.size || ControlSizeTokensEnum.DEFAULT;
  const typographyKey = sizeTypographyMap[
    currentSize
  ] as keyof typeof typographyTokens;

  const typography = (typographyTokens[typographyKey] || {}) as CSSObject;
  const dimensions = (sizeDimensionsMap[currentSize] ||
    sizeDimensionsMap[ControlSizeTokensEnum.DEFAULT]) as CSSObject;

  const resolvedIconSize =
    typeof ownerState.iconSize === "number"
      ? `${ownerState.iconSize}px`
      : ownerState.iconSize;

  return {
    ...typography,
    ...dimensions,

    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    borderRadius: radii[RadiusTokensEnum.SM],
    textDecoration: "none",
    "a:has(&)": {
      textDecoration: "none",
    },

    // --- ICON SIZE (configurable independently of text size) ---
    ...(resolvedIconSize && {
      "& .MuiButton-startIcon, & .MuiButton-endIcon": {
        "& > *:first-of-type": { fontSize: resolvedIconSize },
        "& svg, & img": {
          width: resolvedIconSize,
          height: resolvedIconSize,
          fontSize: resolvedIconSize,
        },
      },
    }),

    // --- TYPE: PRIMARY ---
    ...(ownerState.buttonType === ButtonTypeTokensEnum.PRIMARY && {
      backgroundColor: brandColors[ColorTokensEnum.INTERACTIVE],
      color: brandColors[ColorTokensEnum.WHITE],
      border: `1px solid ${brandColors[ColorTokensEnum.INTERACTIVE]}`,

      "&:hover": {
        backgroundColor: brandColors[ColorTokensEnum.HOVER],
        border: `1px solid ${brandColors[ColorTokensEnum.HOVER]}`,
      },
      "&:focus-visible": {
        outline: "none",
        border: `1px solid ${brandColors[ColorTokensEnum.HOVER]}`,
        boxShadow: `0 0 0 4px ${alpha(brandColors[ColorTokensEnum.HOVER], 0.25)}`,
      },

      ...(ownerState.state === StateTokensEnum.HOVER && {
        backgroundColor: brandColors[ColorTokensEnum.HOVER],
        border: `1px solid ${brandColors[ColorTokensEnum.HOVER]}`,
      }),
      ...(ownerState.state === StateTokensEnum.FOCUS && {
        border: `1px solid ${brandColors[ColorTokensEnum.HOVER]}`,
        boxShadow: `0 0 0 4px ${alpha(brandColors[ColorTokensEnum.HOVER], 0.25)}`,
      }),
      ...(ownerState.state === StateTokensEnum.DANGER && {
        backgroundColor: brandColors[ColorTokensEnum.ERROR],
        border: `1px solid ${brandColors[ColorTokensEnum.ERROR]}`,
        "&:hover": { backgroundColor: brandColors[ColorTokensEnum.ERROR] },
      }),
    }),

    // --- TYPE: SECONDARY ---
    ...(ownerState.buttonType === ButtonTypeTokensEnum.SECONDARY && {
      backgroundColor: brandColors[ColorTokensEnum.WHITE],
      color: brandColors[ColorTokensEnum.INTERACTIVE],
      border: `1px solid ${brandColors[ColorTokensEnum.INTERACTIVE]}`,

      "&:hover": {
        border: `1px solid ${brandColors[ColorTokensEnum.HOVER]}`,
        color: brandColors[ColorTokensEnum.HOVER],
        backgroundColor: brandColors[ColorTokensEnum.WHITE],
      },
      "&:focus-visible": {
        outline: "none",
        border: `1px solid ${brandColors[ColorTokensEnum.HOVER]}`,
        boxShadow: `0 0 0 4px ${alpha(brandColors[ColorTokensEnum.HOVER], 0.25)}`,
      },

      ...(ownerState.state === StateTokensEnum.DANGER && {
        color: brandColors[ColorTokensEnum.ERROR],
        border: `1px solid ${brandColors[ColorTokensEnum.ERROR]}`,
      }),
    }),

    // --- TYPE: TERTIARY ---
    ...(ownerState.buttonType === ButtonTypeTokensEnum.TERTIARY && {
      backgroundColor: "transparent",
      color: brandColors[ColorTokensEnum.INTERACTIVE],
      border: "1px solid transparent",

      "&:hover": {
        color: brandColors[ColorTokensEnum.HOVER],
        backgroundColor: brandColors[ColorTokensEnum.HOVER_LIGHT],
      },
      "&:focus-visible": {
        outline: "none",
        border: `1px solid ${brandColors[ColorTokensEnum.HOVER]}`,
        boxShadow: `0 0 0 4px ${alpha(brandColors[ColorTokensEnum.HOVER], 0.25)}`,
      },
    }),

    // --- DISABLED STATE ---
    "&:disabled": {
      backgroundColor: brandColors[ColorTokensEnum.DISABLED],
      color: brandColors[ColorTokensEnum.WHITE],
      border: "none",
      pointerEvents: "auto",

      "&:hover": {
        cursor: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='%23D50000'><path d='M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8 0-1.85.63-3.55 1.69-4.9L16.9 18.31C15.55 19.37 13.85 20 12 20zm5.31-3.1L6.69 5.69A7.966 7.966 0 0112 4c4.42 0 8 3.58 8 8 0 1.85-.63 3.55-1.69 4.9z'/></svg>") 12 12, not-allowed`,
      },
    },
  };
});

export const GeneralButton = ({
  label,
  size = ControlSizeTokensEnum.DEFAULT,
  buttonType = ButtonTypeTokensEnum.PRIMARY,
  state = StateTokensEnum.DEFAULT,
  iconSize = DEFAULT_ICON_SIZE,
  leftIcon: LeftIcon,
  rightIcon: RightIcon,
  ...props
}: GeneralButtonProps) => {
  return (
    <StyledButton
      ownerState={{ label, size, buttonType, state, iconSize }}
      disabled={state === StateTokensEnum.DISABLED}
      variant="contained"
      disableElevation
      startIcon={LeftIcon ? <LeftIcon /> : undefined}
      endIcon={RightIcon ? <RightIcon /> : undefined}
      {...props}
    >
      {label}
    </StyledButton>
  );
};
